import { getServiceUrl } from '@/app/_utils/select-api-service'

import { RUM_PAYLOAD_VERSION, clampSampleRate } from './constants'

import { extractAttribution } from './attribution'
import { buildContext } from './page-context'
import { getRouteGroup } from './route-group'
import { isSampled } from './sampling'
import type { CollectedMetric, ReportedMetric, RumMetric, RumMetricName, RumPayload } from './types'

const queue = new Map<string, RumMetric>()

let initialized = false
let entryRoute = ''

let sampleRate = 0
let configured = false

export function setRumSampleRate(rate: number): void {
    sampleRate = clampSampleRate(rate)
    configured = true
}

function roundValue(metricName: RumMetricName, value: number): number {
    // CLS is a unitless score well below 1; everything else is milliseconds.
    if (metricName === 'CLS') return Number(value.toFixed(4))

    return Math.round(value)
}

const COLLECTED_METRICS: RumMetricName[] = ['CLS', 'FCP', 'INP', 'LCP', 'TTFB']

function isCollectedMetric(metric: ReportedMetric): metric is CollectedMetric {
    return (COLLECTED_METRICS as string[]).includes(metric.name)
}

export function reportMetric(metric: ReportedMetric): void {
    if (!isCollectedMetric(metric)) return

    const route = window.location.pathname

    queue.set(metric.id, {
        id: metric.id,
        name: metric.name,
        value: roundValue(metric.name, metric.value),
        delta: roundValue(metric.name, metric.delta),
        rating: metric.rating,
        navigationType: metric.navigationType,
        route,
        routeGroup: getRouteGroup(route),
        attribution: extractAttribution(metric),
    })
}

function send(payload: RumPayload): void {
    const body = JSON.stringify(payload)

    const endpoint = `${getServiceUrl('ros')}user-monitoring/web-vitals`

    try {
        const blob = new Blob([body], { type: 'text/plain;charset=UTF-8' })

        if (navigator.sendBeacon?.(endpoint, blob)) return
    } catch {
        // sendBeacon throws in a few browsers instead of returning false.
    }

    try {
        void fetch(endpoint, {
            body,
            method: 'POST',
            credentials: 'omit',
            keepalive: true,
            headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        }).catch(() => undefined)
    } catch {
        // Monitoring must never surface an error into the page.
    }
}

function flush(): void {
    if (!configured) return
    if (queue.size === 0) return

    const metrics = [...queue.values()]
    queue.clear()

    if (!isSampled(sampleRate)) return

    const payload: RumPayload = {
        v: RUM_PAYLOAD_VERSION,
        sentAt: Date.now(),
        context: buildContext(entryRoute, sampleRate),
        metrics,
    }

    send(payload)
}

function handleVisibilityChange(): void {
    if (document.visibilityState === 'hidden') flush()
}

export function initRumLifecycle(): void {
    if (typeof window === 'undefined') return
    if (initialized) return

    initialized = true
    entryRoute = window.location.pathname

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('pagehide', flush)
}
